/**
 * IFRAP-AI - Publication Detail Page
 * Displays a single publication with full content
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  FileText, Calendar, User, Tag, ArrowLeft, Share2, 
  Presentation, ExternalLink, AlertTriangle, Copy, Check
} from 'lucide-react';
import api from '../../api/client';

const PublicationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [publication, setPublication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPublication();
  }, [id]);

  const fetchPublication = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/publications/public/${id}`);
      setPublication(response.data);
    } catch (err) {
      console.error('Error fetching publication:', err);
      setError('Publication non trouvée');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: publication.title,
          text: publication.summary,
          url: url,
        });
      } catch (err) {
        // Share cancelled by user
      }
    } else {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#000091]/20 border-t-[#000091] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !publication) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex flex-col items-center justify-center">
        <FileText className="w-16 h-16 text-gray-300 mb-4" />
        <p className="text-gray-500 mb-4">{error || 'Publication non trouvée'}</p>
        <Link
          to="/publications"
          className="text-[#000091] hover:underline flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour aux publications
        </Link>
      </div>
    );
  }

  const isMocked = publication.tags?.includes('mocké');

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-[#000091] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </button>
            
            <button
              onClick={handleShare}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm transition-colors"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Share2 className="w-4 h-4" />}
              {copied ? 'Copié !' : 'Partager'}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Mocked warning */}
        {isMocked && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">Rapport de démonstration</p>
              <p className="text-sm text-yellow-700">
                Ce contenu a été généré par l'IA à des fins de test. Les données et analyses sont simulées.
              </p>
            </div>
          </div>
        )}

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
          <span className="flex items-center gap-1.5">
            <Calendar className="w-4 h-4" />
            {formatDate(publication.published_at)}
          </span>
          {publication.author_name && (
            <span className="flex items-center gap-1.5">
              <User className="w-4 h-4" />
              {publication.author_name}
            </span>
          )}
          <span className="px-2.5 py-1 bg-[#000091]/10 text-[#000091] rounded-full text-xs font-medium">
            {publication.category || 'Général'}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
          {publication.title}
        </h1>

        {/* Summary */}
        <p className="text-xl text-gray-600 mb-8 leading-relaxed">
          {publication.summary}
        </p>

        {/* Image */}
        {publication.image_url && (
          <div className="mb-8 rounded-xl overflow-hidden">
            <img 
              src={publication.image_url} 
              alt={publication.title}
              className="w-full"
            />
          </div>
        )}

        {/* Gamma link */}
        {publication.gamma_url && (
          <div className="mb-8 p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Presentation className="w-6 h-6 text-purple-600" />
                <div>
                  <p className="font-medium text-purple-900">Présentation Gamma disponible</p>
                  <p className="text-sm text-purple-700">Voir ce rapport en format présentation interactive</p>
                </div>
              </div>
              <a
                href={publication.gamma_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Ouvrir
              </a>
            </div>
          </div>
        )}

        {/* Content */}
        <div 
          className="prose prose-lg max-w-none"
          style={{
            '--tw-prose-headings': '#1a1a2e',
            '--tw-prose-body': '#374151',
          }}
        >
          {/* Render content - could be markdown in the future */}
          {publication.content?.split('\n').map((paragraph, idx) => (
            paragraph.trim() ? (
              <p key={idx}>{paragraph}</p>
            ) : (
              <br key={idx} />
            )
          ))}
        </div>

        {/* Tags */}
        {publication.tags?.length > 0 && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag className="w-4 h-4 text-gray-400" />
              {publication.tags.map((tag, idx) => (
                <span 
                  key={idx}
                  className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">
              Publication IFRAP-AI • {publication.views || 0} vues
            </p>
            <Link
              to="/publications"
              className="flex items-center gap-2 text-[#000091] font-medium hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Toutes les publications
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
};

export default PublicationDetailPage;
